import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiFetchFull } from './client'

// ============================================================
//  Typen — passen 1:1 zu Backend src/modules/stock/sub-resources.ts
// ============================================================

export interface StockPicture {
  id: number
  position: number
  url: string
  is_thumbnail: boolean
  is_large: boolean
}

export interface StockCharacteristic {
  id: number
  position: number
  type: string | null
  value: string | null
}

export interface StockCia {
  id: number
  portal: string
  is_active: boolean
  max_quantity: number | null
  accounts: string[]
  targets: string[]
}

export interface StockKeyword {
  id: number
  keyword: string
  position: number
}

export interface StockFaq {
  id: number
  question: string
  answer: string
  position: number
}

export interface StockCrossSellingItem {
  id: number
  related_dr_id: string | null
  folder_path: string | null
}

// ============================================================
//  Helper — ack-envelope-Response normalisieren
// ============================================================

async function getList<T>(path: string): Promise<T[]> {
  const res = await apiFetchFull<T[]>(path)
  return res.data ?? []
}

// ============================================================
//  Pictures
// ============================================================

export function usePictures(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'pictures'],
    queryFn: () => getList<StockPicture>(`/stock/${encodeURIComponent(sku!)}/images/`),
    enabled: !!sku,
  })
}

export interface PicturePayload {
  url: string
  position?: number
  is_primary?: boolean
  alt_text?: string
}

export function useAddPicture(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: PicturePayload) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/images/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'pictures'] })
    },
  })
}

export function useDeletePicture(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean }>(`/stock/${encodeURIComponent(sku!)}/images/${id}/`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'pictures'] })
    },
  })
}

// ============================================================
//  Characteristics
// ============================================================

export function useCharacteristics(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'characteristics'],
    queryFn: () => getList<StockCharacteristic>(`/stock/${encodeURIComponent(sku!)}/characteristics/`),
    enabled: !!sku,
  })
}

export interface CharacteristicPayload {
  type?: string | null
  value?: string | null
  position?: number
}

export function useAddCharacteristic(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CharacteristicPayload) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/characteristics/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'characteristics'] })
    },
  })
}

export function useUpdateCharacteristic(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CharacteristicPayload }) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/characteristics/${id}/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'characteristics'] })
    },
  })
}

export function useDeleteCharacteristic(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean }>(`/stock/${encodeURIComponent(sku!)}/characteristics/${id}/`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'characteristics'] })
    },
  })
}

// ============================================================
//  CIA
// ============================================================

export function useCia(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'cia'],
    queryFn: () => getList<StockCia>(`/stock/${encodeURIComponent(sku!)}/cia/`),
    enabled: !!sku,
  })
}

export interface CiaPayload {
  portal?: string
  is_active?: boolean
  max_quantity?: number | null
  accounts?: string[]
  targets?: string[]
}

export function useAddCia(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CiaPayload & { portal: string }) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/cia/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'cia'] })
    },
  })
}

export function useUpdateCia(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CiaPayload }) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/cia/${id}/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'cia'] })
    },
  })
}

export function useDeleteCia(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean }>(`/stock/${encodeURIComponent(sku!)}/cia/${id}/`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'cia'] })
    },
  })
}

// ============================================================
//  Keywords
// ============================================================

export function useKeywords(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'keywords'],
    queryFn: () => getList<StockKeyword>(`/stock/${encodeURIComponent(sku!)}/keywords/`),
    enabled: !!sku,
  })
}

export function useAddKeyword(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (keyword: string) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/keywords/`, { keyword }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'keywords'] })
    },
  })
}

export function useDeleteKeyword(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean }>(`/stock/${encodeURIComponent(sku!)}/keywords/${id}/`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'keywords'] })
    },
  })
}

// ============================================================
//  FAQs
// ============================================================

export function useFaqs(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'faqs'],
    queryFn: () => getList<StockFaq>(`/stock/${encodeURIComponent(sku!)}/faqs/`),
    enabled: !!sku,
  })
}

export interface FaqPayload {
  question?: string
  answer?: string
  position?: number
}

export function useAddFaq(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: { question: string; answer: string; position?: number }) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/faqs/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'faqs'] })
    },
  })
}

export function useUpdateFaq(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: FaqPayload }) =>
      api.post<{ id: number }>(`/stock/${encodeURIComponent(sku!)}/faqs/${id}/`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'faqs'] })
    },
  })
}

export function useDeleteFaq(sku: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ deleted: boolean }>(`/stock/${encodeURIComponent(sku!)}/faqs/${id}/`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock', sku, 'faqs'] })
    },
  })
}

// ============================================================
//  Cross-Selling
// ============================================================

export function useCrossSelling(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'cross-selling'],
    queryFn: () => getList<StockCrossSellingItem>(`/stock/${encodeURIComponent(sku!)}/cross-selling/`),
    enabled: !!sku,
  })
}
